import { useState, useEffect, useMemo } from "react";
import { format, eachDayOfInterval, getDay, isFirstDayOfMonth, startOfMonth, subMonths, parseISO, isToday } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { DEMO_GRID_CONFIG } from "./constants";

// Demo mood data - comprehensive and realistic pattern
const DEMO_MOOD_DATA: Record<string, 'Easy' | 'Meh' | 'Struggled'> = {
  // December 2023 - Holiday stress mixed with good days
  "2023-12-01": "Easy",
  "2023-12-02": "Easy",
  "2023-12-03": "Meh",
  "2023-12-05": "Meh",
  "2023-12-06": "Easy",
  "2023-12-08": "Easy",
  "2023-12-09": "Struggled",
  "2023-12-10": "Meh",
  "2023-12-12": "Easy",
  "2023-12-13": "Easy",
  "2023-12-15": "Meh",
  "2023-12-16": "Easy",
  "2023-12-18": "Struggled",
  "2023-12-19": "Struggled",
  "2023-12-20": "Meh",
  "2023-12-22": "Easy",
  "2023-12-23": "Easy",
  "2023-12-24": "Easy",
  "2023-12-26": "Meh",
  "2023-12-27": "Easy",
  "2023-12-28": "Easy",
  "2023-12-29": "Meh",
  "2023-12-30": "Easy",
  "2023-12-31": "Easy",
  
  // November 2023 - Mostly good month with some ups and downs
  "2023-11-01": "Easy",
  "2023-11-02": "Easy",
  "2023-11-03": "Meh",
  "2023-11-04": "Easy",
  "2023-11-05": "Easy",
  "2023-11-06": "Meh",
  "2023-11-07": "Easy",
  "2023-11-08": "Easy",
  "2023-11-09": "Easy",
  "2023-11-10": "Meh",
  "2023-11-11": "Easy",
  "2023-11-12": "Easy",
  "2023-11-13": "Meh",
  "2023-11-14": "Easy",
  "2023-11-15": "Easy",
  "2023-11-16": "Struggled",
  "2023-11-17": "Meh",
  "2023-11-18": "Easy",
  "2023-11-19": "Easy",
  "2023-11-20": "Easy",
  "2023-11-21": "Meh",
  "2023-11-22": "Easy",
  "2023-11-23": "Easy",
  "2023-11-24": "Easy",
  "2023-11-25": "Meh",
  "2023-11-26": "Easy",
  "2023-11-27": "Easy",
  "2023-11-28": "Meh",
  "2023-11-29": "Easy",
  "2023-11-30": "Easy",
  
  // October 2023 - Mixed but trending positive
  "2023-10-01": "Meh",
  "2023-10-02": "Easy",
  "2023-10-03": "Easy",
  "2023-10-04": "Meh",
  "2023-10-05": "Easy",
  "2023-10-06": "Struggled",
  "2023-10-07": "Meh",
  "2023-10-08": "Easy",
  "2023-10-09": "Easy",
  "2023-10-10": "Meh",
  "2023-10-11": "Easy",
  "2023-10-12": "Easy",
  "2023-10-13": "Struggled",
  "2023-10-14": "Easy",
  "2023-10-15": "Meh",
  "2023-10-16": "Easy",
  "2023-10-17": "Easy",
  "2023-10-18": "Meh",
  "2023-10-19": "Easy",
  "2023-10-20": "Struggled",
  "2023-10-21": "Struggled",
  "2023-10-22": "Meh",
  "2023-10-23": "Easy",
  "2023-10-24": "Easy",
  "2023-10-25": "Easy",
  "2023-10-26": "Meh",
  "2023-10-27": "Easy",
  "2023-10-28": "Easy",
  "2023-10-29": "Meh",
  "2023-10-30": "Easy",
  "2023-10-31": "Easy",
  
  // September 2023 - Rough patch with more struggles
  "2023-09-01": "Meh",
  "2023-09-02": "Struggled",
  "2023-09-03": "Meh",
  "2023-09-04": "Struggled",
  "2023-09-05": "Meh",
  "2023-09-06": "Easy",
  "2023-09-07": "Meh",
  "2023-09-08": "Struggled",
  "2023-09-09": "Struggled",
  "2023-09-10": "Struggled",
  "2023-09-11": "Meh",
  "2023-09-12": "Meh",
  "2023-09-13": "Struggled",
  "2023-09-14": "Meh",
  "2023-09-15": "Easy",
  "2023-09-16": "Meh",
  "2023-09-17": "Meh",
  "2023-09-18": "Struggled",
  "2023-09-19": "Meh",
  "2023-09-20": "Easy",
  "2023-09-21": "Meh",
  "2023-09-22": "Struggled",
  "2023-09-23": "Meh",
  "2023-09-24": "Meh",
  "2023-09-25": "Easy",
  "2023-09-26": "Meh",
  "2023-09-27": "Easy",
  "2023-09-28": "Meh",
  "2023-09-29": "Easy",
  "2023-09-30": "Easy",
  
  // August 2023 - Summer vibes, mostly positive
  "2023-08-01": "Easy",
  "2023-08-02": "Easy",
  "2023-08-03": "Easy",
  "2023-08-04": "Meh",
  "2023-08-05": "Easy",
  "2023-08-06": "Easy",
  "2023-08-07": "Easy",
  "2023-08-08": "Easy",
  "2023-08-09": "Meh",
  "2023-08-10": "Easy",
  "2023-08-11": "Easy",
  "2023-08-12": "Easy",
  "2023-08-13": "Easy",
  "2023-08-14": "Meh",
  "2023-08-15": "Easy",
  "2023-08-16": "Easy",
  "2023-08-17": "Easy",
  "2023-08-18": "Meh",
  "2023-08-19": "Easy",
  "2023-08-20": "Easy",
  "2023-08-21": "Easy",
  "2023-08-22": "Meh",
  "2023-08-23": "Easy",
  "2023-08-24": "Easy",
  "2023-08-25": "Struggled",
  "2023-08-26": "Meh",
  "2023-08-27": "Meh",
  "2023-08-28": "Easy",
  "2023-08-29": "Easy",
  "2023-08-30": "Easy",
  "2023-08-31": "Easy",
  
  // July 2023 - Vacation mood, very positive
  "2023-07-01": "Easy",
  "2023-07-02": "Easy",
  "2023-07-03": "Easy",
  "2023-07-04": "Easy",
  "2023-07-05": "Easy",
  "2023-07-06": "Easy",
  "2023-07-07": "Meh",
  "2023-07-08": "Easy",
  "2023-07-09": "Easy",
  "2023-07-10": "Easy",
  "2023-07-11": "Easy",
  "2023-07-12": "Easy",
  "2023-07-13": "Meh",
  "2023-07-14": "Easy",
  "2023-07-15": "Easy",
  "2023-07-16": "Easy",
  "2023-07-17": "Easy",
  "2023-07-18": "Easy",
  "2023-07-19": "Meh",
  "2023-07-20": "Easy",
  "2023-07-21": "Easy",
  "2023-07-22": "Easy",
  "2023-07-23": "Easy",
  "2023-07-24": "Easy",
  "2023-07-25": "Meh",
  "2023-07-26": "Easy",
  "2023-07-27": "Easy",
  "2023-07-28": "Easy",
  "2023-07-29": "Easy",
  "2023-07-30": "Easy",
  "2023-07-31": "Easy",
  
  // June 2023 - Building momentum
  "2023-06-01": "Meh",
  "2023-06-02": "Meh",
  "2023-06-03": "Easy",
  "2023-06-04": "Meh",
  "2023-06-05": "Easy",
  "2023-06-06": "Meh",
  "2023-06-07": "Easy",
  "2023-06-08": "Easy",
  "2023-06-09": "Meh",
  "2023-06-10": "Easy",
  "2023-06-11": "Easy",
  "2023-06-12": "Easy",
  "2023-06-13": "Meh",
  "2023-06-14": "Easy",
  "2023-06-15": "Easy",
  "2023-06-16": "Struggled",
  "2023-06-17": "Meh",
  "2023-06-18": "Easy",
  "2023-06-19": "Easy",
  "2023-06-20": "Easy",
  "2023-06-21": "Meh",
  "2023-06-22": "Easy",
  "2023-06-23": "Easy",
  "2023-06-24": "Meh",
  "2023-06-25": "Easy",
  "2023-06-26": "Easy",
  "2023-06-27": "Easy",
  "2023-06-28": "Meh",
  "2023-06-29": "Easy",
  "2023-06-30": "Easy",
  
  // May 2023 - Starting to track more consistently
  "2023-05-01": "Meh",
  "2023-05-02": "Meh",
  "2023-05-03": "Struggled",
  "2023-05-04": "Meh",
  "2023-05-05": "Easy",
  "2023-05-06": "Meh",
  "2023-05-07": "Meh",
  "2023-05-08": "Easy",
  "2023-05-09": "Meh",
  "2023-05-10": "Struggled",
  "2023-05-11": "Meh",
  "2023-05-12": "Easy",
  "2023-05-13": "Meh",
  "2023-05-14": "Easy",
  "2023-05-15": "Meh",
  "2023-05-16": "Easy",
  "2023-05-17": "Struggled",
  "2023-05-18": "Meh",
  "2023-05-19": "Easy",
  "2023-05-20": "Meh",
  "2023-05-21": "Easy",
  "2023-05-22": "Easy",
  "2023-05-23": "Meh",
  "2023-05-24": "Easy",
  "2023-05-25": "Struggled",
  "2023-05-26": "Meh",
  "2023-05-27": "Easy",
  "2023-05-28": "Meh",
  "2023-05-29": "Easy",
  "2023-05-30": "Easy",
  "2023-05-31": "Meh",
  
  // April 2023 - First attempts, sporadic tracking
  "2023-04-02": "Meh",
  "2023-04-05": "Struggled",
  "2023-04-08": "Meh",
  "2023-04-09": "Meh",
  "2023-04-12": "Easy",
  "2023-04-15": "Meh",
  "2023-04-18": "Struggled",
  "2023-04-21": "Meh",
  "2023-04-23": "Easy",
  "2023-04-26": "Meh",
  "2023-04-29": "Easy"
};

// Use the same reference date as habit grid
const DEMO_END_DATE = parseISO("2023-12-31");

const moodColors = {
  Easy: { filled: 'hsl(142, 71%, 45%)' }, // Green
  Meh: { filled: 'hsl(24, 95%, 65%)' }, // Orange
  Struggled: { filled: 'hsl(0, 90%, 65%)' }, // Red
  empty: 'hsl(var(--habit-empty))'
};

export default function DemoMoodTracker() {
  const [gridData, setGridData] = useState<(Date | null)[][]>([]);
  const [months, setMonths] = useState<{name: string, index: number}[]>([]);
  const isMobile = useIsMobile();
  
  // Calculate total tracked days
  const totalTrackedDays = useMemo(() => {
    return Object.keys(DEMO_MOOD_DATA).length;
  }, []);

  // Generate the grid data (same structure as habit grid)
  useEffect(() => {
    const endDate = DEMO_END_DATE;
    const startDate = startOfMonth(subMonths(endDate, DEMO_GRID_CONFIG.MONTHS_TO_SHOW - 1));
    
    const allDates = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    
    const monthsData: {name: string, index: number}[] = [];
    const grid: (Date | null)[][] = Array(7).fill(null).map(() => []);
    
    let currentMonth = "";
    let weekIndex = 0;
    
    allDates.forEach((date) => {
      const dayOfWeek = getDay(date);
      
      if (dayOfWeek === 0 && grid[0].length > 0) {
        weekIndex++;
      }
      
      if (isFirstDayOfMonth(date)) {
        const monthName = format(date, 'MMM');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          monthsData.push({
            name: monthName,
            index: weekIndex
          });
        }
      }
      
      for (let i = 0; i < 7; i++) {
        while (grid[i].length <= weekIndex) {
          grid[i].push(null);
        }
      }
      
      grid[dayOfWeek][weekIndex] = date;
    });
    
    setGridData(grid);
    setMonths(monthsData);
  }, []);

  // Get mood for a specific date
  const getMoodForDate = (date: Date | null): 'Easy' | 'Meh' | 'Struggled' | null => {
    if (!date) return null;
    const dateString = format(date, 'yyyy-MM-dd');
    return DEMO_MOOD_DATA[dateString] || null;
  };

  // Get color for a cell
  const getCellColor = (date: Date | null): string => {
    if (!date) return 'transparent';
    
    const mood = getMoodForDate(date);
    if (!mood) return moodColors.empty;
    
    return moodColors[mood].filled;
  };

  // Weekday labels
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const cellSize = isMobile ? DEMO_GRID_CONFIG.CELL_SIZE.MOBILE : DEMO_GRID_CONFIG.CELL_SIZE.DESKTOP;
  const cellGap = isMobile ? DEMO_GRID_CONFIG.CELL_GAP.MOBILE : DEMO_GRID_CONFIG.CELL_GAP.DESKTOP;
  const weekdayColumnWidth = isMobile ? DEMO_GRID_CONFIG.WEEKDAY_COLUMN.MOBILE : DEMO_GRID_CONFIG.WEEKDAY_COLUMN.DESKTOP;
  const rowHeight = isMobile ? DEMO_GRID_CONFIG.ROW_HEIGHT.MOBILE : DEMO_GRID_CONFIG.ROW_HEIGHT.DESKTOP;

  return (
    <div className="demo-mood-tracker mt-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium">Daily Mood</h3>
          <span className="text-sm text-muted-foreground">
            {totalTrackedDays} days tracked
          </span>
        </div>
      </div>
      
      <div className="overflow-x-auto w-full">
        <div className="min-w-max">
          {/* Month labels */}
          <div className="flex h-6 relative mb-1">
            <div className={`${weekdayColumnWidth} mr-2`}></div>
            <div className="flex-1 relative">
              {months.map((month, i) => (
                <div 
                  key={i} 
                  className="absolute text-xs text-muted-foreground"
                  style={{ left: `${month.index * (isMobile ? 15 : 20)}px` }}
                >
                  {month.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex">
            {/* Weekday labels */}
            <div className={`flex flex-col ${weekdayColumnWidth} mr-2`}>
              {weekdays.map((day, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center text-xs text-muted-foreground leading-none mb-1"
                  style={{ height: rowHeight }}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Grid cells */}
            <div className="flex flex-col">
              {gridData.map((row, rowIndex) => (
                <div 
                  key={`row-${rowIndex}`} 
                  className={`flex items-center ${cellGap} mb-1`} 
                  style={{ height: rowHeight }}
                >
                  {row.map((date, colIndex) => {
                    const mood = getMoodForDate(date);
                    const dateStr = date ? format(date, 'MMMM d, yyyy') : '';
                    const moodStatus = mood ? `Mood: ${mood}` : 'No mood tracked';
                    const glowEffect = mood ? "shadow-[0_0_5px_0px_rgba(255,255,255,0.3)] dark:shadow-[0_0_8px_0px_rgba(255,255,255,0.15)]" : "";
                    const isTodayDate = date ? isToday(date) : false;
                    const todayBorder = isTodayDate ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : "";
                    
                    return (
                      <div
                        key={`cell-${rowIndex}-${colIndex}`}
                        className={`${cellSize} rounded-sm transition-all hover:scale-125 hover:z-10 hover:shadow-md cursor-pointer grid-cell ${glowEffect} ${todayBorder}`}
                        style={{ 
                          backgroundColor: getCellColor(date),
                          visibility: date ? 'visible' : 'hidden'
                        }}
                        title={date ? `${dateStr} - ${moodStatus}${isTodayDate ? ' (Today)' : ''}` : ''}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Legend and Sign-up prompt */}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: moodColors.Easy.filled }}
            />
            <span className="text-sm">Easy</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: moodColors.Meh.filled }}
            />
            <span className="text-sm">Meh</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: moodColors.Struggled.filled }}
            />
            <span className="text-sm">Struggled</span>
          </div>
        </div>
        <div className="text-sm text-muted-foreground italic">
          Sign up for deeper habit-emotion analysis
        </div>
      </div>
    </div>
  );
} 